
const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "/api"
).replace(/\/+$/, "");

const TOKEN_KEY = "stxaviersbela_admin_token";

/* =====================================================
   AUTHENTICATION
===================================================== */

export function getAdminToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function setAdminToken(token) {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // Storage may be unavailable.
  }
}

/* =====================================================
   API REQUEST HANDLER
===================================================== */

async function request(path, options = {}) {
  const headers = new Headers(options.headers || {});
  const token = getAdminToken();

  const isFormData = options.body instanceof FormData;

  if (!isFormData && options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    body:
      isFormData || options.body === undefined
        ? options.body
        : JSON.stringify(options.body),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(
      payload.message || `Request failed (${response.status}).`
    );

    error.status = response.status;
    error.payload = payload;

    throw error;
  }

  return payload;
}

/* =====================================================
   MEDIA URL RESOLVER
   Fix PDF opening homepage instead of document
===================================================== */

export function resolveMediaUrl(url) {
  if (!url || typeof url !== "string") {
    return "";
  }

  const value = url.trim();

  if (!value) {
    return "";
  }

  // Preserve absolute URLs and browser media URLs.
  if (/^(https?:|data:|blob:)/i.test(value)) {
    return value;
  }

  // Normalize API base.
  const apiBase = (
    process.env.REACT_APP_API_URL || "/api"
  ).replace(/\/+$/, "");

  let backendOrigin = apiBase.replace(/\/api$/i, "");

  // In local development, connect uploaded files
  // directly to the Node.js backend.
  if (
    process.env.NODE_ENV === "development" &&
    !backendOrigin
  ) {
    backendOrigin =
      process.env.REACT_APP_BACKEND_URL ||
      "http://localhost:5000";
  }

  backendOrigin = backendOrigin.replace(/\/+$/, "");

  // Handle:
  // /uploads/file.pdf
  // uploads/file.pdf
  if (/^\/?uploads\//i.test(value)) {
    const normalizedPath =
      "/" + value.replace(/^\/+/, "");

    return `${backendOrigin}${normalizedPath}`;
  }

  return value;
}

/* =====================================================
   API ENDPOINTS
===================================================== */

export const api = {

  /* ---------------- AUTHENTICATION ---------------- */

  login: (body) =>
    request("/auth/login", {
      method: "POST",
      body,
    }),

  me: () => request("/auth/me"),

  stats: () => request("/admin/stats"),

  /* ---------------- BANNERS ---------------- */

  activeBanners: () =>
    request("/banners/active"),

  banners: {
    list: () =>
      request("/banners"),

    create: (body) =>
      request("/banners", {
        method: "POST",
        body,
      }),

    update: (id, body) =>
      request(`/banners/${id}`, {
        method: "PUT",
        body,
      }),

    remove: (id) =>
      request(`/banners/${id}`, {
        method: "DELETE",
      }),
  },

  /* ---------------- GALLERY ---------------- */

  activeGalleries: () =>
    request("/galleries/active"),

  galleries: {
    list: () =>
      request("/galleries"),

    create: (body) =>
      request("/galleries", {
        method: "POST",
        body,
      }),

    update: (id, body) =>
      request(`/galleries/${id}`, {
        method: "PUT",
        body,
      }),

    remove: (id) =>
      request(`/galleries/${id}`, {
        method: "DELETE",
      }),
  },

  /* ---------------- ENQUIRIES ---------------- */

  submitEnquiry: (body) =>
    request("/enquiries", {
      method: "POST",
      body,
    }),

  enquiries: {
    list: () =>
      request("/enquiries"),

    status: (id, status) =>
      request(`/enquiries/${id}/status`, {
        method: "PATCH",
        body: { status },
      }),

    remove: (id) =>
      request(`/enquiries/${id}`, {
        method: "DELETE",
      }),
  },

  /* ---------------- NEWS ---------------- */

  activeNews: () =>
    request("/news/active"),

  news: {
    list: () =>
      request("/news"),

    create: (body) =>
      request("/news", {
        method: "POST",
        body,
      }),

    update: (id, body) =>
      request(`/news/${id}`, {
        method: "PUT",
        body,
      }),

    remove: (id) =>
      request(`/news/${id}`, {
        method: "DELETE",
      }),
  },

  /* ---------------- FEE STRUCTURE ---------------- */

  activeFeeStructure: () =>
    request("/fee-structure/active"),

  feeStructures: {
    list: () =>
      request("/fee-structure"),

    create: (body) =>
      request("/fee-structure", {
        method: "POST",
        body,
      }),

    update: (id, body) =>
      request(`/fee-structure/${id}`, {
        method: "PUT",
        body,
      }),

    remove: (id) =>
      request(`/fee-structure/${id}`, {
        method: "DELETE",
      }),
  },

  /* ---------------- MANDATORY DISCLOSURES ---------------- */

  // Public website
  activeMandatoryDisclosures: () =>
    request("/mandatory-disclosures/active"),

  // Admin dashboard
  mandatoryDisclosures: {
    list: () =>
      request("/mandatory-disclosures"),

    create: (body) =>
      request("/mandatory-disclosures", {
        method: "POST",
        body,
      }),

    update: (id, body) =>
      request(`/mandatory-disclosures/${id}`, {
        method: "PUT",
        body,
      }),

    remove: (id) =>
      request(`/mandatory-disclosures/${id}`, {
        method: "DELETE",
      }),
  },

  /* ---------------- VIDEO GALLERY ---------------- */

  activeVideos: () =>
    request("/video-gallery/active"),

  videos: {
    list: () =>
      request("/video-gallery"),

    create: (body) =>
      request("/video-gallery", {
        method: "POST",
        body,
      }),

    update: (id, body) =>
      request(`/video-gallery/${id}`, {
        method: "PUT",
        body,
      }),

    remove: (id) =>
      request(`/video-gallery/${id}`, {
        method: "DELETE",
      }),
  },

  /* ---------------- CBSE DETAILS ---------------- */

  activeCbseDetails: () =>
    request("/cbse-details/active"),

  cbseDetails: {
    list: () =>
      request("/cbse-details"),

    create: (body) =>
      request("/cbse-details", {
        method: "POST",
        body,
      }),

    update: (id, body) =>
      request(`/cbse-details/${id}`, {
        method: "PUT",
        body,
      }),

    remove: (id) =>
      request(`/cbse-details/${id}`, {
        method: "DELETE",
      }),
  },

  /* ---------------- TRANSFER CERTIFICATES ---------------- */

  // Public TC verification
  verifyTransferCertificate: (body) =>
    request("/transfer-certificates/verify", {
      method: "POST",
      body,
    }),

  // Admin TC management
  transferCertificates: {
    list: () =>
      request("/transfer-certificates"),

    create: (body) =>
      request("/transfer-certificates", {
        method: "POST",
        body,
      }),

    update: (id, body) =>
      request(`/transfer-certificates/${id}`, {
        method: "PUT",
        body,
      }),

    remove: (id) =>
      request(`/transfer-certificates/${id}`, {
        method: "DELETE",
      }),
  },

  /* ---------------- IMAGE UPLOAD ---------------- */

  uploadImage: (file) => {
    const formData = new FormData();

    formData.append("image", file);

    return request("/uploads/image", {
      method: "POST",
      body: formData,
    });
  },

  /* ---------------- PDF DOCUMENT UPLOAD ---------------- */

  uploadDocument: (file) => {
    const formData = new FormData();

    formData.append("document", file);

    return request("/uploads/document", {
      method: "POST",
      body: formData,
    });
  },
};
